import React, { useEffect, useState } from "react";
import { LogOut, BarChart2, PieChart } from "react-feather";
import { Capacitor } from "@capacitor/core";
import { useTheme } from "../../theme/ThemeContext";
import { useSettings } from "../../settings/SettingsContext";
import { useAuth } from "../../auth/AuthContext";
import { cancelAllReminders, isNativeApp, getReminderStatus, requestReminderPermission, openExactAlarmSettings, sendTestReminder } from "../../tasks/notifications";
import GroupsSection from "./GroupsSection";
import SmsImportCard from "./SmsImport";
import { forgetDevice } from "../../push/push";
import DangerZone from "../../settings/DangerZone";
import { clearSmsMemory } from "../../sms/smsInbox";
import { Card, SectionTitle, Button, Field, useInputStyle } from "../ui";

const Toggle = ({ label, hint, checked, onChange }) => {
  const { tokens } = useTheme();
  return (
    <button
      onClick={() => onChange(!checked)}
      style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "12px 0", background: "none", border: "none", textAlign: "left", cursor: "pointer", color: tokens.textPrimary }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 15, fontWeight: 600 }}>{label}</div>
        {hint && <div style={{ fontSize: 12, color: tokens.textMuted, marginTop: 2 }}>{hint}</div>}
      </div>
      <span style={{ width: 44, height: 26, borderRadius: 13, position: "relative", backgroundColor: checked ? tokens.accent : tokens.neutralSoft, transition: "background-color 0.15s" }}>
        <span style={{ position: "absolute", top: 3, left: checked ? 21 : 3, width: 20, height: 20, borderRadius: 10, backgroundColor: checked ? tokens.accentContrast : tokens.textMuted, transition: "left 0.15s" }} />
      </span>
    </button>
  );
};

const NotificationsCard = () => {
  const { tokens } = useTheme();
  const [status, setStatus] = useState(null);
  const [note, setNote] = useState("");

  useEffect(() => {
    getReminderStatus().then(setStatus);
  }, []);

  if (!isNativeApp() || !status) return null;

  const allowed = status.notifications === "granted";
  const test = async () => {
    setNote("");
    const sent = await sendTestReminder();
    setNote(sent ? "Sent. It will appear in about 8 seconds. Lock your phone to see it on the lock screen." : "Turn on notifications first.");
  };

  return (
    <>
      <SectionTitle>Phone notifications</SectionTitle>
      <Card>
        <div style={{ fontSize: 14, color: tokens.textPrimary, marginBottom: 4 }}>
          {allowed ? "Task reminders are on. They show on your lock screen and at the top of the screen." : "Task reminders are off. Turn them on to get them outside the app."}
        </div>
        {allowed && status.exact !== "granted" && (
          <div style={{ fontSize: 12, color: tokens.pending, margin: "8px 0" }}>
            Android may delay reminders by a few minutes. Allow "Alarms &amp; reminders" for exact timing.
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
          {!allowed && <Button onClick={async () => setStatus(await requestReminderPermission())}>Turn on notifications</Button>}
          {allowed && status.exact !== "granted" && (
            <Button kind="ghost" onClick={async () => { await openExactAlarmSettings(); setTimeout(async () => setStatus(await getReminderStatus()), 1500); }}>Allow exact timing</Button>
          )}
          {allowed && <Button kind="ghost" onClick={test}>Send a test notification</Button>}
        </div>
        {note && <div style={{ marginTop: 10, fontSize: 12, color: tokens.textMuted }}>{note}</div>}
        {allowed && Capacitor.getPlatform() === "android" && (
          <div style={{ marginTop: 10, fontSize: 12, color: tokens.textMuted }}>
            If reminders never arrive, open Android Settings → Apps → budgetsprint → Battery and choose "Unrestricted".
          </div>
        )}
        {allowed && Capacitor.getPlatform() === "ios" && (
          <div style={{ marginTop: 10, fontSize: 12, color: tokens.textMuted }}>
            On iPhone, reminders are set on this phone, so open the app now and then to keep them up to date. Alerts from other people arrive while the app is open.
          </div>
        )}
      </Card>
    </>
  );
};
const MoreScreen = ({ data, groupsApi, isAdmin, goTo }) => {
  const { tokens, theme, toggleTheme } = useTheme();
  const { settings, updateSettings } = useSettings();
  const { logout } = useAuth();
  const input = useInputStyle();
  const [name, setName] = useState(settings.profileName);
  const [email, setEmail] = useState(settings.profileEmail);
  const [saved, setSaved] = useState(false);
  const [profileError, setProfileError] = useState("");

  const saveProfile = async () => {
    setProfileError("");
    const result = await updateSettings({ profileName: name.trim(), profileEmail: email.trim() });
    if (!result.ok) return setProfileError(result.error);
    setSaved(true);
    setTimeout(() => setSaved(false), 1800);
  };
  return (
    <div>
      {isAdmin && (
        <>
          <SectionTitle>Admin</SectionTitle>
          <Button onClick={() => goTo("admin")} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <BarChart2 size={16} /> User analytics
          </Button>
        </>
      )}

      <SectionTitle>Insights</SectionTitle>
      <Button kind="ghost" onClick={() => goTo("analysis")} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
        <PieChart size={16} /> Spending analysis
      </Button>

      <GroupsSection groupsApi={groupsApi} />

      <SectionTitle>Profile</SectionTitle>
      <Card>
        <Field label="Name"><input style={input} value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <Field label="Email (you can sign in with it)"><input style={input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoCapitalize="none" /></Field>
        {profileError && <div role="alert" style={{ marginBottom: 10, fontSize: 13, color: tokens.negative }}>{profileError}</div>}
        <Button onClick={saveProfile} style={{ width: "100%" }}>{saved ? "Saved ✓" : "Save profile"}</Button>
      </Card>

      <SmsImportCard data={data} />

      <NotificationsCard />

      <SectionTitle>Preferences</SectionTitle>
      <Card style={{ padding: "4px 16px" }}>
        <Toggle label="Hide amounts" hint="Show ₹•••• instead of real numbers" checked={settings.maskAmounts} onChange={(v) => updateSettings({ maskAmounts: String(v) })} />
        <div style={{ height: 1, backgroundColor: tokens.surfaceBorder }} />
        <Toggle label="Autopay reminders" hint="Ask me to confirm autopay bills that passed their date" checked={settings.autopayReminders} onChange={(v) => updateSettings({ autopayReminders: String(v) })} />
        <div style={{ height: 1, backgroundColor: tokens.surfaceBorder }} />
        <Toggle label={theme === "dark" ? "Dark theme" : "Light theme"} checked={theme === "dark"} onChange={toggleTheme} />
      </Card>

      <SectionTitle>App</SectionTitle>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <Button kind="danger" onClick={async () => { cancelAllReminders(); await forgetDevice(); logout(); }} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <LogOut size={16} /> Log out
        </Button>
      </div>

      <SectionTitle>Reset or delete</SectionTitle>
      {/* The phone's memory of imported SMS goes too, so they can be read in again. */}
      <DangerZone
        onReset={() => { clearSmsMemory(); data.reload(); }}
        beforeLogout={async () => { clearSmsMemory(); cancelAllReminders(); await forgetDevice(); }}
      />
    </div>
  );
};

export default MoreScreen;
